import {
  AdminApiError,
  type AdminClient,
  type LeaderboardOverride,
  type LeaderboardResponse,
} from '@apx/admin-sdk';
import { prettyGame } from '../games.js';
import { leaderboardSchema } from '@apx/integrator-config/schemas';
import { askConfirm, askPrompt } from '../modal.js';
import { fmtDateTime, fmtNumber, fmtTime } from '../format.js';
import { showToast } from '../toast.js';

export interface LeaderboardPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
}

const DEFAULT_VALUE: LeaderboardOverride = {
  windowHours: 24,
  topN: 10,
  refreshIntervalMs: 5_000,
};

export async function renderLeaderboardPage(
  root: HTMLElement,
  deps: LeaderboardPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Leaderboard Config — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Top-N players by income (sum of payouts) or turnover (sum of bets) over a rolling window. The greedyking iframe's <code>TOP WINNERS</code> modal renders this feed in place of the old <code>generateMockWinners</code> stub.</p>

    <div class="card">
      <h2>Configuration</h2>
      <div class="form-grid">
        <label>Window (hours)
          <input id="lb-window" type="number" min="1" max="720" />
        </label>
        <label>Top N
          <input id="lb-top-n" type="number" min="1" max="100" />
        </label>
        <label>Refresh interval (ms)
          <input id="lb-refresh" type="number" min="1000" max="60000" step="500" />
        </label>
      </div>
      <div class="row">
        <button id="lb-save" class="primary">Propose change</button>
        <button id="lb-reset" class="ghost">Reset to defaults</button>
        <button id="lb-reset-window" class="ghost">Reset window (clear cache)</button>
        <span id="lb-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Live preview — income</h2>
      <div id="lb-income"><p class="muted">Loading…</p></div>
    </div>

    <div class="card">
      <h2>Live preview — turnover</h2>
      <div id="lb-turnover"><p class="muted">Loading…</p></div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="lb-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const windowEl = root.querySelector<HTMLInputElement>('#lb-window')!;
  const topNEl = root.querySelector<HTMLInputElement>('#lb-top-n')!;
  const refreshEl = root.querySelector<HTMLInputElement>('#lb-refresh')!;
  const msgEl = root.querySelector<HTMLElement>('#lb-msg')!;
  const incomeEl = root.querySelector<HTMLElement>('#lb-income')!;
  const turnoverEl = root.querySelector<HTMLElement>('#lb-turnover')!;

  function fillForm(value: LeaderboardOverride): void {
    windowEl.value = String(value.windowHours);
    topNEl.value = String(value.topN);
    refreshEl.value = String(value.refreshIntervalMs);
  }

  function readForm(): LeaderboardOverride {
    return {
      windowHours: Number(windowEl.value) || 24,
      topN: Number(topNEl.value) || 10,
      refreshIntervalMs: Number(refreshEl.value) || 5000,
    };
  }

  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'leaderboard');
  fillForm((current as LeaderboardOverride | null) ?? DEFAULT_VALUE);

  function renderBoard(target: HTMLElement, board: LeaderboardResponse): void {
    if (board.entries.length === 0) {
      target.innerHTML = '<p class="muted">No entries in the configured window.</p>';
      return;
    }
    target.innerHTML = `<table>
      <thead><tr><th>#</th><th>Player</th><th>${board.metric === 'income' ? 'Payout' : 'Turnover'}</th></tr></thead>
      <tbody>${board.entries
        .map(
          (e) => `<tr>
            <td>${e.rank}</td>
            <td>${e.displayName}</td>
            <td>${fmtNumber(e.value)}</td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>
    <p class="muted" style="margin-top:6px">Window: last ${board.windowHours}h · Top ${board.topN} · Generated ${fmtTime(board.generatedAt)}</p>`;
  }

  async function refreshLive(): Promise<void> {
    try {
      const [income, turnover] = await Promise.all([
        deps.client.fetchLeaderboard(deps.roundEngineBaseUrl, deps.integratorId, deps.gameId, 'income'),
        deps.client.fetchLeaderboard(deps.roundEngineBaseUrl, deps.integratorId, deps.gameId, 'turnover'),
      ]);
      renderBoard(incomeEl, income);
      renderBoard(turnoverEl, turnover);
    } catch (err) {
      incomeEl.innerHTML = `<p class="error">${
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err)
      }</p>`;
    }
  }

  root.querySelector<HTMLButtonElement>('#lb-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    msgEl.textContent = 'Reset to defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#lb-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = leaderboardSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => e.message).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'leaderboard',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  root.querySelector<HTMLButtonElement>('#lb-reset-window')!.addEventListener('click', async () => {
    if (!(await askConfirm('Clear the cached leaderboard snapshot? Next request recomputes from scratch.'))) return;
    try {
      const res = await deps.client.resetLeaderboard(deps.integratorId, deps.gameId);
      msgEl.textContent = `Cache reset (${res.deletedKeys} keys dropped).`;
      msgEl.className = 'muted';
      await refreshLive();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'leaderboard' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#lb-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Pending</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td><code>${JSON.stringify(a.pendingConfig)}</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'leaderboard');
          fillForm((fresh.value as LeaderboardOverride | null) ?? DEFAULT_VALUE);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  await refreshLive();
  await refreshPending();
}
